import React from 'react';
import ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import './globals.css';
import { installDataLayerSanitizer } from './lib/analytics/sanitizeDataLayer';
import { initGa4 } from './lib/analytics/gtag';
import { initPostHog } from './lib/analytics/posthog';
import { installPartnerRefCapture } from './lib/partners/partnerRef';
import { clearLocalAuthSession, isInvalidAuthSessionError } from './lib/supabase';
import './services/modules/whatsappEmbeddedSignup';

installDataLayerSanitizer();
installPartnerRefCapture();
initPostHog();
initGa4();

if (import.meta.env.PROD && typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  // One-shot: limpa SW/precache que ainda rodavam o host partykit.dev (DNS morto).
  // index.html já é max-age=0; o problema era sw.js cacheado ~4h no edge + tab viva.
  const PWA_BUST_KEY = 'adlitics:pwa-bust:partykit-2026-10-29';
  const bustStalePwa = async (): Promise<boolean> => {
    try {
      if (window.localStorage.getItem(PWA_BUST_KEY)) return false;
      window.localStorage.setItem(PWA_BUST_KEY, '1');
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      if ('caches' in window) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      }
      return true;
    } catch {
      return false;
    }
  };

  void (async () => {
    if (await bustStalePwa()) {
      window.location.reload();
      return;
    }

    // autoUpdate + update periódico: sw.js agora é max-age=0 no _headers.
    registerSW({
      immediate: true,
      onRegisteredSW(_url, registration) {
        if (!registration) return;
        const checkForUpdate = () => {
          void registration.update().catch(() => {
            console.warn('[PWA] Atualização indisponível; nova tentativa no próximo ciclo.');
          });
        };
        checkForUpdate();
        window.setInterval(checkForUpdate, 5 * 60 * 1000);
      },
    });
  })();
}

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (!isInvalidAuthSessionError(event.reason)) return;
    event.preventDefault();
    void clearLocalAuthSession();
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}


const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
